import React from "react";
import { TrainingChecklist } from "./TrainingChecklist.jsx";

/** @typedef {import('../helpers/resolvePageHelp.js').PageHelpTopic} PageHelpTopic */

const DEFAULT_LABELS = {
  screenPrefix: "Vous êtes dans :",
  titlePrefix: "Aide —",
  purpose: "Objectif de cette page",
  actions: "Ce que vous pouvez faire ici",
  checklist: "À vérifier avant de continuer",
  mistakes: "Erreurs fréquentes",
  nextStep: "Prochaine étape recommandée",
  usefulButtons: "Boutons utiles",
};

/**
 * @param {object} props
 * @param {PageHelpTopic} props.topic
 * @param {Partial<typeof DEFAULT_LABELS>} [props.labels]
 */
export function PageHelpContent({ topic, labels: labelOverrides = {} }) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };

  return (
    <div className="cq-page-help-content" data-testid="cq-page-help-content">
      <header className="cq-page-help-content__head">
        <p className="cq-page-help-content__eyebrow" data-testid="cq-help-screen-label">
          {labels.screenPrefix} {topic.screenLabel}
        </p>
        <h2 className="cq-page-help-content__title" data-testid="cq-help-title">
          {labels.titlePrefix} {topic.screenLabel}
        </h2>
      </header>

      {topic.purpose ? (
        <section className="cq-page-help-section" data-testid="cq-help-purpose">
          <h3>{labels.purpose}</h3>
          <p>{topic.purpose}</p>
        </section>
      ) : null}

      {topic.actions?.length ? (
        <section className="cq-page-help-section" data-testid="cq-help-actions">
          <h3>{labels.actions}</h3>
          <ol className="cq-page-help__actions">
            {topic.actions.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ol>
        </section>
      ) : null}

      {topic.checklist?.length ? (
        <section className="cq-page-help-section" data-testid="cq-help-checklist">
          <h3>{labels.checklist}</h3>
          <TrainingChecklist items={topic.checklist} testId="cq-help-checklist-items" />
        </section>
      ) : null}

      {topic.commonMistakes?.length ? (
        <section className="cq-page-help-section" data-testid="cq-help-mistakes">
          <h3>{labels.mistakes}</h3>
          <ul className="cq-page-help__bullets">
            {topic.commonMistakes.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {topic.warning ? (
        <section className="cq-page-help-section cq-page-help-section--warn" data-testid="cq-help-warning">
          <p>{topic.warning}</p>
        </section>
      ) : null}

      {topic.nextStep ? (
        <section className="cq-page-help-section" data-testid="cq-help-next-step">
          <h3>{labels.nextStep}</h3>
          <p>{topic.nextStep}</p>
        </section>
      ) : null}

      {topic.usefulButtons?.length ? (
        <section className="cq-page-help-section" data-testid="cq-help-buttons">
          <h3>{labels.usefulButtons}</h3>
          <p className="cq-page-help__muted">{topic.usefulButtons.join(" · ")}</p>
        </section>
      ) : null}
    </div>
  );
}
